/**
 * API клиент для общения с бэкендом Летописи
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const AUTH_USE_COOKIE_REFRESH_RAW = (import.meta.env.VITE_AUTH_USE_COOKIE_REFRESH ?? 'true')
  .toString()
  .toLowerCase();
const AUTH_USE_COOKIE_REFRESH =
  AUTH_USE_COOKIE_REFRESH_RAW !== 'false' && AUTH_USE_COOKIE_REFRESH_RAW !== '0';

const MEDIA_USE_PRESIGNED_RAW = (import.meta.env.VITE_MEDIA_USE_PRESIGNED ?? 'true')
  .toString()
  .toLowerCase();
const MEDIA_USE_PRESIGNED = MEDIA_USE_PRESIGNED_RAW !== 'false' && MEDIA_USE_PRESIGNED_RAW !== '0';
const MEDIA_USE_PRESIGNED_UPLOAD_RAW = (
  import.meta.env.VITE_MEDIA_USE_PRESIGNED_UPLOAD ?? import.meta.env.VITE_MEDIA_USE_PRESIGNED ?? 'true'
)
  .toString()
  .toLowerCase();
const MEDIA_USE_PRESIGNED_UPLOAD = 
  MEDIA_USE_PRESIGNED_UPLOAD_RAW !== 'false' && MEDIA_USE_PRESIGNED_UPLOAD_RAW !== '0'; 

const MEDIA_USE_MULTIPART_UPLOAD_RAW = (import.meta.env.VITE_MEDIA_USE_MULTIPART_UPLOAD ?? 'true')
  .toString()
  .toLowerCase();
const MEDIA_USE_MULTIPART_UPLOAD =
  MEDIA_USE_MULTIPART_UPLOAD_RAW !== 'false' && MEDIA_USE_MULTIPART_UPLOAD_RAW !== '0';

const MEDIA_MULTIPART_THRESHOLD_MB = Number.parseInt(
  import.meta.env.VITE_MEDIA_MULTIPART_THRESHOLD_MB ?? '200',
  10,
);
const MEDIA_MULTIPART_THRESHOLD_BYTES = Number.isFinite(MEDIA_MULTIPART_THRESHOLD_MB)
  ? MEDIA_MULTIPART_THRESHOLD_MB * 1024 * 1024
  : 200 * 1024 * 1024;

const MEDIA_MULTIPART_CONCURRENCY = Math.max(
  1,
  Number.parseInt(import.meta.env.VITE_MEDIA_MULTIPART_CONCURRENCY ?? '3', 10) || 3,
);
const PRESIGNED_URL_SKEW_MS = 60_000;
const presignedUrlCache = new Map(); // key -> { url, expiresAtMs }
const CSRF_COOKIE_NAME = 'letopis_csrf';
const AUTH_STORAGE_KEY = 'letopis-auth';
let memoryAccessToken = null;

const readAuthStorageState = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeAuthStorageState = (nextState) => {
  if (typeof localStorage === 'undefined') return;
  if (!nextState || typeof nextState !== 'object') {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextState));
};

const updateAuthStorageState = (updater) => {
  if (AUTH_USE_COOKIE_REFRESH) {
    return;
  }
  const currentState = readAuthStorageState() || {};
  writeAuthStorageState(updater(currentState));
};

export const getStoredRefreshToken = () => {
  if (AUTH_USE_COOKIE_REFRESH) {
    return null;
  }
  return readAuthStorageState()?.state?.refreshToken || null;
};

const setStoredRefreshToken = (token) => {
  updateAuthStorageState((state) => ({
    ...state,
    state: {
      ...(state.state || {}),
      refreshToken: token || null,
    },
  }));
};

export const setApiAccessToken = (token) => {
  memoryAccessToken = token || null;
  updateAuthStorageState((state) => ({
    ...state,
    state: {
      ...(state.state || {}),
      accessToken: memoryAccessToken,
    },
  }));
};

export const getApiAccessToken = () => {
  if (memoryAccessToken) return memoryAccessToken;
  if (AUTH_USE_COOKIE_REFRESH) return null;
  return readAuthStorageState()?.state?.accessToken || null;
};

export const clearApiAccessToken = () => {
  setApiAccessToken(null);
};

const isAbsoluteHttpUrl = (url) => typeof url === 'string' && /^https?:\/\//i.test(url);

const normalizeProtectedAssetUrl = (sourceUrl) => {
  if (!sourceUrl || typeof sourceUrl !== 'string') return null;

  if (isAbsoluteHttpUrl(sourceUrl)) {
    if (typeof window !== 'undefined' && sourceUrl.startsWith(window.location.origin)) {
      sourceUrl = sourceUrl.slice(window.location.origin.length);
    } else {
      return null;
    }
  }

  if (sourceUrl.startsWith(API_BASE_URL)) {
    const trimmed = sourceUrl.slice(API_BASE_URL.length);
    return trimmed || '/';
  }

  if (sourceUrl.startsWith('/')) {
    return sourceUrl;
  }

  return `/${sourceUrl}`;
};

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const getCookieValue = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const getCsrfHeaders = () => {
  if (!AUTH_USE_COOKIE_REFRESH) return {};
  const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
  if (!csrfToken) return {};
  return { 'X-CSRF-Token': csrfToken };
};

export const hasRefreshSessionContext = (state = null) => {
  const resolvedState = state || readAuthStorageState()?.state || {};

  if (AUTH_USE_COOKIE_REFRESH) {
    return Boolean(getCookieValue(CSRF_COOKIE_NAME) || resolvedState?.isAuthenticated || resolvedState?.user);
  }

  return Boolean(resolvedState?.refreshToken || getStoredRefreshToken());
};

const uploadFileToPresignedUrl = (uploadUrl, file, requiredHeaders = {}, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    Object.entries(requiredHeaders || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value));
    });
    if (typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        onProgress(clampPercent(Math.round((event.loaded / event.total) * 100)));
      };
      onProgress(0);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === 'function') onProgress(100);
        resolve();
      }
      else reject(new Error(`Presigned upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Presigned upload failed: network error'));
    xhr.send(file);
  });

const uploadBlobToPresignedUrl = (uploadUrl, blob, requiredHeaders = {}, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    Object.entries(requiredHeaders || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value));
    });
    if (typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        onProgress({ loaded: event.loaded, total: event.total });
      };
      onProgress({ loaded: 0, total: blob?.size ?? 0 });
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader('ETag') });
        return;
      }
      const error = new Error(`Presigned upload failed: HTTP ${xhr.status}`);
      error.status = xhr.status;
      reject(error);
    };
    xhr.onerror = () => reject(new Error('Presigned upload failed: network error'));
    xhr.onabort = () => reject(new Error('Presigned upload aborted'));
    xhr.send(blob);
  });

const runWithConcurrency = async (items, concurrency, worker) => {
  const safeConcurrency = Math.max(1, Number(concurrency) || 1);
  const results = new Array(items.length);
  let nextIndex = 0;

  const runners = Array.from(
    { length: Math.min(safeConcurrency, items.length) },
    async () => {
      while (true) {
        const currentIndex = nextIndex;
        if (currentIndex >= items.length) return;
        nextIndex += 1;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    },
  );

  await Promise.all(runners);
  return results;
};

// Создаём экземпляр axios
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: AUTH_USE_COOKIE_REFRESH,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authHttp = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: AUTH_USE_COOKIE_REFRESH,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена
api.interceptors.request.use((config) => {
  // Читаем из Zustand persisted state (ключ 'letopis-auth')
  const token = getApiAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


// Интерцептор для обработки ошибок и refresh токена
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const prevState = readAuthStorageState()?.state ?? {};
    if (!hasRefreshSessionContext(prevState)) {
      throw new Error('No refresh session context');
    }

    const refreshToken = getStoredRefreshToken() || prevState.refreshToken;

    if (!AUTH_USE_COOKIE_REFRESH && !refreshToken) {
      throw new Error('No refresh token available');
    }


    const payload = AUTH_USE_COOKIE_REFRESH ? {} : { refresh_token: refreshToken };
    const response = await authHttp.post('/auth/refresh', payload, {
      headers: getCsrfHeaders(),
    });

    const { access_token, refresh_token } = response.data;

    setApiAccessToken(access_token);
    setStoredRefreshToken(AUTH_USE_COOKIE_REFRESH ? null : refresh_token);
    updateAuthStorageState((state) => ({
      ...state,
      state: {
        ...(state.state || prevState),
        isAuthenticated: true,
      },
    }));


    return access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const isAuthEndpoint = (url = '') =>
  url.includes('/auth/login') ||
  url.includes('/auth/refresh') ||
  url.includes('/auth/register') ||
  url.includes('/auth/demo-login');

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url ?? '';

    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint(url)) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        clearApiAccessToken();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ==================== Auth ====================

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (refreshToken) =>
    api.post('/auth/refresh', refreshToken ? { refresh_token: refreshToken } : {}, {
      withCredentials: AUTH_USE_COOKIE_REFRESH,
      headers: getCsrfHeaders(),
    }),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  requestEmailChange: (newEmail, password) => api.post('/auth/request-email-change', { new_email: newEmail, password }),
  confirmEmailChange: (token) => api.post('/auth/confirm-email-change', { token }),
  logout: (refreshToken) =>
    api.post('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}, {
      withCredentials: AUTH_USE_COOKIE_REFRESH,
      headers: getCsrfHeaders(),
    }),
  demoLogin: () => api.post('/auth/demo-login'),
};

// ==================== Users ====================

export const usersAPI = {
  getMe: () => api.get('/users/me'),
  updateMe: (data) => api.patch('/users/me', data),
  deleteAccount: (password) => api.delete('/users/me', { data: { password } }),
  getMyFamilies: () => api.get('/users/me/families'),
  getMyAvatarBlobUrl: async () => {
    const response = await api.get('/users/me/avatar/file', {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: () => api.delete('/users/me/avatar'),
};

export const assetsAPI = {
  getProtectedBlobUrl: async (sourceUrl) => {
    const requestUrl = normalizeProtectedAssetUrl(sourceUrl);
    if (!requestUrl) return null;

    try {
      const response = await api.get(requestUrl, {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`Failed to load protected asset ${sourceUrl}:`, error?.response?.status || error.message);
      return null;
    }
  },
};

// ==================== Admin ====================

export const adminAPI = {
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.patch('/admin/settings', data),
  testEmail: () => api.post('/admin/test-email'),
};

// ==================== Families ====================

export const familiesAPI = {
  list: () => api.get('/families'),
  create: (data) => api.post('/families', data),
  get: (id) => api.get(`/families/${id}`),
  update: (id, data) => api.patch(`/families/${id}`, data),
  delete: (id) => api.delete(`/families/${id}`),
  
  // Members
  listMembers: (familyId) => api.get(`/families/${familyId}/members`),
  updateMember: (familyId, memberId, data) =>
    api.patch(`/families/${familyId}/members/${memberId}`, data),
  removeMember: (familyId, memberId) =>
    api.delete(`/families/${familyId}/members/${memberId}`),

  // Current user member actions
  linkPersonToMe: (familyId, personId) =>
    api.patch(`/families/${familyId}/members/me/link`, null, { params: { person_id: personId } }),
  unlinkPersonFromMe: (familyId) =>
    api.patch(`/families/${familyId}/members/me/link`, null, { params: { person_id: null } }),
  leaveFamily: (familyId) =>
    api.delete(`/families/${familyId}/members/me`),

  // Invitations
  createInvitation: (familyId, data) =>
    api.post(`/families/${familyId}/invitations`, data),
  listInvitations: (familyId) =>
    api.get(`/families/${familyId}/invitations`),
  revokeInvitation: (familyId, invitationId) =>
    api.delete(`/families/${familyId}/invitations/${invitationId}`),

  // Statistics
  getStatistics: (familyId) =>
    api.get(`/families/${familyId}/statistics`),

  // Map locations
  getMapLocations: (familyId) =>
    api.get(`/families/${familyId}/map`),
};

// ==================== Persons ====================

export const personsAPI = {
  list: (familyId, params = {}) => 
    api.get(`/persons/family/${familyId}`, { params }),
  create: (familyId, data) => 
    api.post(`/persons/family/${familyId}`, data),
  get: (id) => api.get(`/persons/${id}`),
  update: (id, data) => api.patch(`/persons/${id}`, data),
  delete: (id) => api.delete(`/persons/${id}`),
  
  // Relations
  createRelation: (data) => api.post('/persons/relations', data),
  deleteRelation: (id) => api.delete(`/persons/relations/${id}`),
  
  // Tree
  getTree: (familyId) => api.get(`/persons/family/${familyId}/tree`),
  
  // Common ancestors
  findCommonAncestors: (data) => api.post('/persons/common-ancestors', data),
};

// ==================== Stories ====================

export const storiesAPI = {
  listByFamily: (familyId, params = {}) =>
    api.get(`/stories/family/${familyId}`, { params }),
  listByPerson: (personId, params = {}) =>
    api.get(`/stories/person/${personId}`, { params }),
  create: (data) => api.post('/stories', data),
  get: (id) => api.get(`/stories/${id}`),
  update: (id, data) => api.patch(`/stories/${id}`, data),
  delete: (id) => api.delete(`/stories/${id}`),

  // Audio stories
  uploadAudio: (personId, file, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', metadata.title || 'Аудио-история');
    if (metadata.summary) formData.append('summary', metadata.summary);
    if (metadata.event_date) formData.append('event_date', metadata.event_date);

    return api.post(`/stories/audio/upload/${personId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAudioUrl: async (storyId) => {
    const response = await api.get(`/stories/${storyId}/audio/file`, {
      responseType: 'blob',
    });

    return {
      data: {
        url: URL.createObjectURL(response.data),
      },
    };
  },

  // Comments
  listComments: (storyId) => api.get(`/stories/${storyId}/comments`),
  addComment: (storyId, data) => api.post(`/stories/${storyId}/comments`, data),
};

// ==================== Media ====================

const MULTIPART_RESUME_STATE_VERSION = 1;
const MULTIPART_RESUME_STORAGE_PREFIX = 'letopis-media-multipart';
const MULTIPART_PRESIGN_BATCH_SIZE = 25;
const MULTIPART_PROGRESS_THROTTLE_MS = 200;

const getMultipartResumeStorageKey = (personId, file) => {
  const name = encodeURIComponent(file?.name ?? 'upload');
  const size = typeof file?.size === 'number' ? file.size : 0;
  const lastModified = typeof file?.lastModified === 'number' ? file.lastModified : 0;
  const type = encodeURIComponent(file?.type ?? '');
  return `${MULTIPART_RESUME_STORAGE_PREFIX}:${personId}:${name}:${size}:${lastModified}:${type}`;
};

const readMultipartResumeState = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeMultipartResumeState = (storageKey, state) => {
  localStorage.setItem(storageKey, JSON.stringify(state));
};

const clearMultipartResumeState = (storageKey) => {
  localStorage.removeItem(storageKey);
};

const multipartUploadMedia = async (personId, file, metadata = {}, options = {}) => {
  const onProgress = options?.onProgress;
  const fileSize = typeof file?.size === 'number' ? file.size : 0;
  if (!fileSize || fileSize <= 0) {
    throw new Error('Multipart upload requires a File with a valid size');
  }

  const storageKey = getMultipartResumeStorageKey(personId, file);
  const stored = readMultipartResumeState(storageKey);

  const contentType = file?.type || 'application/octet-stream';
  const filename = file?.name || 'upload';

  let key = stored?.key || null;
  let uploadId = stored?.upload_id || null;
  let partSize = stored?.part_size || null;

  let alreadyUploadedParts = [];
  if (key && uploadId && partSize) {
    try {
      const response = await api.get(`/media/multipart/parts/${personId}`, {
        params: { key, upload_id: uploadId },
      });
      alreadyUploadedParts = response?.data?.parts || [];
    } catch {
      clearMultipartResumeState(storageKey);
      key = null;
      uploadId = null;
      partSize = null;
    }
  }

  if (!key || !uploadId || !partSize) {
    const response = await api.post(`/media/multipart/create/${personId}`, {
      filename,
      content_type: contentType,
      size: fileSize,
    });
    key = response?.data?.key;
    uploadId = response?.data?.upload_id;
    partSize = response?.data?.part_size;
    if (!key || !uploadId || !partSize) {
      throw new Error('Multipart create response missing key/upload_id/part_size');
    }
    alreadyUploadedParts = [];
  }

  const resumeState = {
    version: MULTIPART_RESUME_STATE_VERSION,
    created_at: stored?.created_at || Date.now(),
    updated_at: Date.now(),
    person_id: String(personId),
    key,
    upload_id: uploadId,
    part_size: partSize,
    file: {
      name: filename,
      size: fileSize,
      last_modified: typeof file?.lastModified === 'number' ? file.lastModified : null,
      type: file?.type || null,
    },
  };
  writeMultipartResumeState(storageKey, resumeState);

  const partCount = Math.ceil(fileSize / partSize);
  const etagByPart = new Map();
  let totalLoadedBytes = 0;

  for (const part of alreadyUploadedParts) {
    if (!part?.part_number || !part?.etag) continue;
    if (part.part_number < 1 || part.part_number > partCount) continue;
    etagByPart.set(part.part_number, part.etag);
    if (typeof part.size === 'number' && Number.isFinite(part.size) && part.size > 0) {
      totalLoadedBytes += part.size;
    }
  }
  totalLoadedBytes = Math.min(fileSize, Math.max(0, totalLoadedBytes));

  let lastProgressPercent = -1;
  let lastProgressAt = 0;
  const emitProgress = (force = false) => {
    if (typeof onProgress !== 'function') return;
    const percent = clampPercent(Math.round((totalLoadedBytes / fileSize) * 100));
    const now = Date.now();
    if (!force) {
      if (percent === lastProgressPercent) return;
      if (now - lastProgressAt < MULTIPART_PROGRESS_THROTTLE_MS && percent !== 100) return;
    }
    lastProgressPercent = percent;
    lastProgressAt = now;
    onProgress(percent);
  };
  emitProgress(true);

  const remainingPartNumbers = [];
  for (let partNumber = 1; partNumber <= partCount; partNumber += 1) {
    if (!etagByPart.has(partNumber)) remainingPartNumbers.push(partNumber);
  }

  const inFlightLoaded = new Map(); // partNumber -> loaded bytes

  const uploadPart = async (partNumber, uploadUrl) => {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(fileSize, start + partSize);
    const blob = file.slice(start, end);

    const response = await uploadBlobToPresignedUrl(uploadUrl, blob, {}, (progress) => {
      const loaded = Math.max(0, Math.min(blob.size, progress?.loaded ?? 0));
      const prevLoaded = inFlightLoaded.get(partNumber) || 0;
      const delta = loaded - prevLoaded;
      if (delta > 0) {
        totalLoadedBytes = Math.min(fileSize, totalLoadedBytes + delta);
        inFlightLoaded.set(partNumber, loaded);
        emitProgress();
      }
    });

    const finalizedLoaded = inFlightLoaded.get(partNumber) || 0;
    if (finalizedLoaded < blob.size) {
      totalLoadedBytes = Math.min(fileSize, totalLoadedBytes + (blob.size - finalizedLoaded));
      inFlightLoaded.set(partNumber, blob.size);
      emitProgress();
    }

    const etag = response?.etag;
    if (!etag) {
      throw new Error(
        'Missing ETag header for multipart part upload (check MinIO CORS: ExposeHeaders must include ETag)',
      );
    }
    etagByPart.set(partNumber, etag);
  };

  for (let i = 0; i < remainingPartNumbers.length; i += MULTIPART_PRESIGN_BATCH_SIZE) {
    const batch = remainingPartNumbers.slice(i, i + MULTIPART_PRESIGN_BATCH_SIZE);
    const presignResponse = await api.post(`/media/multipart/presign/${personId}`, {
      key,
      upload_id: uploadId,
      part_numbers: batch,
    });
    const parts = presignResponse?.data?.parts || [];
    const urlByPart = new Map(parts.map((part) => [part.part_number, part.upload_url]));

    await runWithConcurrency(batch, MEDIA_MULTIPART_CONCURRENCY, async (partNumber) => {
      const url = urlByPart.get(partNumber);
      if (!url) throw new Error(`Missing presigned URL for part ${partNumber}`);
      await uploadPart(partNumber, url);
    });
  }

  if (etagByPart.size !== partCount) {
    throw new Error(`Multipart upload incomplete: expected ${partCount} parts, got ${etagByPart.size}`);
  }

  const parts = Array.from(etagByPart.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([part_number, etag]) => ({ part_number, etag }));

  const completeResponse = await api.post(`/media/multipart/complete/${personId}`, {
    key,
    upload_id: uploadId,
    parts,
    original_filename: filename,
    title: metadata.title || null,
    description: metadata.description || null,
    event_date: metadata.event_date || null,
    location: metadata.location || null,
  });

  clearMultipartResumeState(storageKey);
  emitProgress(true);
  return completeResponse;
};

export const mediaAPI = {
  upload: async (personId, file, metadata = {}, options = {}) => {
    const onProgress = options?.onProgress;
    const shouldUseMultipart =
      MEDIA_USE_PRESIGNED_UPLOAD &&
      MEDIA_USE_MULTIPART_UPLOAD &&
      typeof file?.size === 'number' &&
      file.size >= MEDIA_MULTIPART_THRESHOLD_BYTES;

    if (shouldUseMultipart) {
      try {
        return await multipartUploadMedia(personId, file, metadata, options);
      } catch (error) {
        console.warn('Multipart upload failed, falling back to single PUT upload:', error);
      }
    }

    if (MEDIA_USE_PRESIGNED_UPLOAD) {
      try {
        const contentType = file?.type || 'application/octet-stream';
        const presignResponse = await api.post(`/media/presign/upload/${personId}`, {
          filename: file?.name || 'upload',
          content_type: contentType,
          size: typeof file?.size === 'number' ? file.size : null,
        });

        const { key, upload_url, required_headers } = presignResponse.data || {};
        if (!key || !upload_url) {
          throw new Error('Presign response missing key/upload_url');       
        }

        await uploadFileToPresignedUrl(upload_url, file, required_headers, onProgress); 

        return await api.post(`/media/complete/${personId}`, {
          key,
          original_filename: file?.name || 'upload',
          title: metadata.title || null,
          description: metadata.description || null,
          event_date: metadata.event_date || null,
          location: metadata.location || null,
        });
      } catch (error) {
        console.warn('Presigned upload failed, falling back to API upload:', error);
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });

    return api.post(`/media/upload/${personId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (typeof onProgress !== 'function') return;
        if (!event.total) return;
        onProgress(clampPercent(Math.round((event.loaded / event.total) * 100)));
      },
    });
  },
  listByFamily: (familyId, params = {}) =>
    api.get(`/media/family/${familyId}`, { params }),
  listByPerson: (personId, params = {}) =>
    api.get(`/media/person/${personId}`, { params }),
  get: (id) => api.get(`/media/${id}`),
  reprocess: (id) => api.post(`/media/${id}/reprocess`),
  update: (id, data) => api.patch(`/media/${id}`, data),
  delete: (id) => api.delete(`/media/${id}`),
  getUrl: (id, version = 'original') => {
    if (!id) {
      console.warn('mediaAPI.getUrl called with empty id');
      return Promise.resolve({ data: { url: null } });
    }
    return api.get(`/media/${id}/url`, { params: { version } });
  },

  // Получение URL для <img>/<video>/<audio>:
  // - в проде: presigned URL на MinIO (data plane)
  // - в dev: fallback на blob URL через авторизованный запрос (control plane)
  getViewUrl: async (id, version = 'original') => {
    if (!id || id === 'undefined' || id === 'null' || id === '') {
      console.warn('getViewUrl called with invalid id:', id);
      return null;
    }

    if (MEDIA_USE_PRESIGNED) {
      const cacheKey = `${id}:${version}`;
      const cached = presignedUrlCache.get(cacheKey);
      if (cached?.url && cached?.expiresAtMs && cached.expiresAtMs > Date.now() + PRESIGNED_URL_SKEW_MS) {
        return cached.url;
      }

      try {
        const response = await api.get(`/media/${id}/url`, { params: { version } });
        const url = response.data?.url;
        const expiresIn = response.data?.expires_in;
        if (isAbsoluteHttpUrl(url)) {
          if (typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0) {
            presignedUrlCache.set(cacheKey, { url, expiresAtMs: Date.now() + expiresIn * 1000 });
          }
          return url;
        }
      } catch (error) {
        console.error(
          `Failed to get presigned URL for id=${id}:`,
          error?.response?.status || error.message,
        );
      }
    }

    return await mediaAPI.getFileBlobUrl(id, version);
  },

  invalidatePresignedUrlCache: (id, version) => {
    if (!id) return;
    if (!MEDIA_USE_PRESIGNED) return;
    if (version) {
      presignedUrlCache.delete(`${id}:${version}`);
      return;
    }
    // Remove all cached variants for this media id (original/thumbnail/etc).
    for (const key of presignedUrlCache.keys()) {
      if (key.startsWith(`${id}:`)) presignedUrlCache.delete(key);
    }
  },

  // Получение файла как blob URL (через axios с авторизацией)
  // Это решает проблему с <img src> который не отправляет Authorization header
  getFileBlobUrl: async (id, version = 'original') => {
    // Validate ID before making request - check for null, undefined, empty, or string versions
    if (!id || id === 'undefined' || id === 'null' || id === '') {
      console.warn('getFileBlobUrl called with invalid id:', id);
      return null;
    }
    try {
      const response = await api.get(`/media/${id}/file`, {
        params: { version },
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`Failed to get file blob for id=${id}:`, error?.response?.status || error.message);
      return null;
    }
  },
};

// ==================== Invitations ====================

export const invitationsAPI = {
  // Получение информации о приглашении (публичный endpoint)
  getInfo: (token) => api.get(`/invitations/${token}`),
  // Получение приглашения по короткому коду (XXXX-XXXX)
  getByCode: (shortCode) => api.get(`/invitations/code/${shortCode}`),
  // Принятие приглашения (требует авторизации)
  // data: { link_person: boolean, person_updates?: {...} }
  accept: (token, data = {}) => api.post(`/invitations/${token}/accept`, data),
};

// ==================== Notifications ====================

export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// ==================== Reminders ====================

export const remindersAPI = {
  getFamily: (familyId, params = {}) =>
    api.get(`/reminders/family/${familyId}`, { params }),
  getSettings: () => api.get('/reminders/settings'),
  updateSettings: (data) => api.patch('/reminders/settings', data),
  sendFamily: (familyId, params = {}) =>
    api.post(`/reminders/family/${familyId}/send`, null, { params }),
  testFamily: (familyId, params = {}) =>
    api.post(`/reminders/family/${familyId}/test`, null, { params }),
  getHistory: (familyId, params = {}) =>
    api.get(`/reminders/family/${familyId}/history`, { params }),
  getCalendarIcs: (familyId, params = {}) =>
    api.get(`/reminders/family/${familyId}/ics`, { params, responseType: 'blob' }),
  // "On This Day" - events that happened on this day in family history
  getOnThisDay: (familyId, params = {}) =>
    api.get(`/reminders/family/${familyId}/on-this-day`, { params }),
};

// ==================== Feed ====================

export const feedAPI = {
  getFamily: (familyId, params = {}) =>
    api.get(`/feed/family/${familyId}`, { params }),
};

// ==================== Publications ====================

export const publicationsAPI = {
  // List publication requests for a family
  list: (familyId, status = null) => {
    const params = status ? { status } : {};
    return api.get(`/family/${familyId}/publications`, { params });
  },
  // Create publication request
  create: (familyId, data) => api.post(`/family/${familyId}/publications`, data),
  // Get single publication request
  get: (requestId) => api.get(`/publications/${requestId}`),
  // Vote on publication request
  vote: (requestId, data) => api.post(`/publications/${requestId}/vote`, data),
  // Get votes for a request
  listVotes: (requestId) => api.get(`/publications/${requestId}/votes`),
  // Cancel publication request
  cancel: (requestId) => api.delete(`/publications/${requestId}`),
};

// ==================== Public ====================

export const publicAPI = {
  // Get public family by slug
  getFamily: (slug) => api.get(`/public/family/${slug}`),
  // Check if family exists
  checkFamily: (slug) => api.get(`/public/family/${slug}/exists`),
};

// ==================== Export ====================

export const exportAPI = {
  // GEDCOM format (standard genealogy)
  gedcom: (familyId) =>
    api.get(`/export/family/${familyId}/gedcom`, { responseType: 'blob' }),

  // JSON backup
  json: (familyId, includeStories = true) =>
    api.get(`/export/family/${familyId}/json`, {
      params: { include_stories: includeStories },
      responseType: 'blob'
    }),

  // CSV export
  csv: (familyId) =>
    api.get(`/export/family/${familyId}/csv`, { responseType: 'blob' }),

  // PDF export (family tree document)
  pdf: (familyId, includeDetails = true) =>
    api.get(`/export/family/${familyId}/pdf`, {
      params: { include_details: includeDetails },
      responseType: 'blob'
    }),

  // Family Book export (complete printable book)
  book: (familyId, includeStories = true) =>
    api.get(`/export/family/${familyId}/book`, {
      params: { include_stories: includeStories },
      responseType: 'blob'
    }),
};

// ==================== Import ====================

export const importAPI = {
  csv: (familyId, file, allowDuplicates = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/family/${familyId}/csv`, formData, {
      params: { allow_duplicates: allowDuplicates },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  gedcom: (familyId, file, allowDuplicates = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/import/family/${familyId}/gedcom`, formData, {
      params: { allow_duplicates: allowDuplicates },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ==================== Timeline ====================

export const timelineAPI = {
  // Get family timeline
  getFamily: (familyId, params = {}) =>
    api.get(`/timeline/family/${familyId}`, { params }),

  // Get person timeline
  getPerson: (personId) =>
    api.get(`/timeline/person/${personId}`),
};

// ==================== Face Recognition ====================

export const faceAPI = {
  // Detect faces in a media file
  detect: (mediaId) => api.post(`/faces/detect/${mediaId}`),

  // Get detection status for a media file
  getStatus: (mediaId) => api.get(`/faces/status/${mediaId}`),

  // Confirm a face tag
  confirm: (detectedFaceId, personId, saveEmbedding = true) =>
    api.post('/faces/confirm', {
      detected_face_id: detectedFaceId,
      person_id: personId,
      save_embedding: saveEmbedding,
    }),

  // Reject a detected face
  reject: (detectedFaceId, reason = null) =>
    api.post('/faces/reject', {
      detected_face_id: detectedFaceId,
      reason,
    }),

  // Get face samples for a person
  getPersonSamples: (personId) => api.get(`/faces/person/${personId}/samples`),

  // Delete a face embedding
  deleteEmbedding: (embeddingId) => api.delete(`/faces/embedding/${embeddingId}`),
};

// ==================== AI Processing ====================

export const aiAPI = {
  // Start AI processing job
  // jobType: 'colorize', 'restore', 'enhance', 'animate'
  process: (mediaId, jobType) =>
    api.post(`/ai/process/${mediaId}`, { job_type: jobType }),

  // Get job status
  getJob: (jobId) => api.get(`/ai/jobs/${jobId}`),

  // Get all AI versions of a media file
  getVersions: (mediaId) => api.get(`/ai/media/${mediaId}/versions`),

  // Get all jobs for a media file
  listJobs: (mediaId) => api.get(`/ai/media/${mediaId}/jobs`),

  // Get AI output by canonical media version URL or by legacy AI job output endpoint.
  getOutputBlobUrl: async (outputUrlOrJobId) => {
    if (!outputUrlOrJobId) return null;
    const rawTarget = String(outputUrlOrJobId);
    const mediaMatch = rawTarget.match(/\/api\/v1\/media\/([^/?#]+)\/file(?:\?([^#]+))?$/);
    if (mediaMatch) {
      const mediaId = mediaMatch[1];
      const params = new URLSearchParams(mediaMatch[2] || '');
      const version = params.get('version') || 'original';
      return await mediaAPI.getViewUrl(mediaId, version);
    }

    const normalizedTarget = rawTarget.startsWith('/api/v1/')
      ? rawTarget.replace(/^\/api\/v1/, '')
      : rawTarget;
    const target = normalizedTarget.includes('/') ? normalizedTarget : `/ai/output/${normalizedTarget}`;
    try {
      const response = await api.get(target, {
        responseType: 'blob',
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(
        `Failed to get AI output for target=${target}:`,
        error?.response?.status || error.message,
      );
      return null;
    }
  },
};


// ==================== Gamification (Family Games) ====================

export const gamesAPI = {
  // Get gamification dashboard for a family
  getDashboard: (familyId) => api.get(`/games/dashboard/${familyId}`),

  // Get all achievements for a user in a family
  getAchievements: (familyId, includeLocked = true) =>
    api.get(`/games/achievements/${familyId}`, { params: { include_locked: includeLocked } }),

  // Get leaderboard for a family
  getLeaderboard: (familyId, period = 'all_time', limit = 20) =>
    api.get(`/games/leaderboard/${familyId}`, { params: { period, limit } }),

  // Get available quizzes
  getQuizzes: (familyId) => api.get(`/games/quizzes/${familyId}`),

  // Generate a new quiz
  generateQuiz: (familyId, quizType, difficulty = 'medium', questionsCount = 5) =>
    api.post(`/games/quizzes/generate`, {
      quiz_type: quizType,
      difficulty,
      questions_count: questionsCount,
    }, { params: { family_id: familyId } }),

  // Get quiz for playing (with questions)
  getQuizForPlay: (quizId) => api.get(`/games/quizzes/${quizId}/play`),

  // Submit quiz answers
  submitQuiz: (quizId, answers) =>
    api.post(`/games/quizzes/${quizId}/submit`, { answers }),

  // Get available quests
  getQuests: (familyId) => api.get(`/games/quests/${familyId}`),

  // Get user's quests
  getMyQuests: (familyId, status = null) =>
    api.get(`/games/quests/${familyId}/my`, { params: status ? { status_filter: status } : {} }),

  // Create a new quest (editors only)
  createQuest: (familyId, questData) =>
    api.post(`/games/quests`, questData, { params: { family_id: familyId } }),

  // Start a quest
  startQuest: (questId) => api.post(`/games/quests/${questId}/start`),
};

export default api;
